import { isDocumentNode } from '@apollo/client/utilities';
import { DeeplyReadonly, Query } from '@cubejs-client/core';
import { UseCubeQueryOptions } from '@cubejs-client/react';
import { CircularProgress } from '@mui/material';
import { renderProp, RenderPropType } from '@procureai/ui-mui';
import { DocumentNode } from 'graphql';
import { useMemo } from 'react';
import { ListChildComponentProps } from 'react-window';

import {
  useVariableCubeQuery,
  useVariableGraphQuery,
  UseGraphVariableQueryOptions,
} from '~/hooks/useVariableQuery/useVariableQuery';

import { LoadingContainer, FixedSizeListStyled } from './VirtualList.styled';

export type Item = {
  id: string;
  [propName: string]: any;
};

export interface VirtualListProps<I extends Item = Item> {
  query: DeeplyReadonly<Query> | DocumentNode;
  queryOptions: UseGraphVariableQueryOptions<any, any> | UseCubeQueryOptions;
  renderItem: RenderPropType<{ item: I; virtual?: boolean; index?: number }>;
  itemSize: number;
}

interface VirtualListRowsProps<I extends Item = Item> extends VirtualListProps {
  rows: I[];
  loading: boolean;
}

const VirtualListRows = <I extends Item = Item>({
  renderItem,
  rows,
  loading,
  ...listProps
}: VirtualListRowsProps<I>) => {
  const renderRow = useMemo(() => {
    if (rows) {
      return (props: ListChildComponentProps) => {
        const { index, style } = props;
        const item = rows[index];
        return (
          <div
            style={{
              ...style,
              left: 20,
              right: 20,
              width: 'auto',
            }}
            key={item.id}
          >
            {renderProp(renderItem, { key: item.id, item, index, virtual: true })}
          </div>
        );
      };
    }
    return null;
  }, [rows, renderItem]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return renderRow ? (
    <FixedSizeListStyled itemCount={rows.length} height={400} width={500} {...listProps}>
      {renderRow}
    </FixedSizeListStyled>
  ) : null;
};

const VirtualGraphList = (props: VirtualListProps) => {
  const { rows, loading } = useVariableGraphQuery(props.query as DocumentNode, props.queryOptions);

  return <VirtualListRows rows={rows} loading={loading} {...props} />;
};

const VirtualCubeList = (props: VirtualListProps) => {
  const { rows, loading } = useVariableCubeQuery(props.query as DeeplyReadonly<Query>, props.queryOptions);
  return <VirtualListRows rows={rows} loading={loading} {...props} />;
};

export const VirtualList = (props: VirtualListProps) => {
  if (isDocumentNode(props.query)) {
    return <VirtualGraphList {...props} />;
  } else {
    return <VirtualCubeList {...props} />;
  }
};
